import { extractJson } from '../utils/jsonParser.js';

const VALID_FIELDS = ['science', 'art', 'sports', 'politics', 'business', 'entertainment', 'literature', 'military', 'fictional', 'other'];
const VALID_DIFFICULTIES = ['easy', 'medium', 'hard'];

const GENERATION_SYSTEM_PROMPT = `You are a "20 questions" style celebrity guessing game host. Your job is to pick a single well-known figure and describe them with a JSON object.

The target may be either:
- A real, famous person (historical or contemporary), OR
- A famous fictional character from novels, movies, TV series, video games, animation, anime/manga, fairy tales, myths, legends, or folk tales (e.g. 孙悟空, 哈利·波特, 马里奥).

Rules:
1. Choose someone widely known. Obscure figures make the game frustrating.
2. Do NOT include any clues or hints that could directly reveal the name.
3. The "brief" field must be a one-sentence summary but must NOT include the name. For fictional characters it must also NOT include the title of the source work.
4. Return ONLY valid JSON, no markdown code fences, no extra commentary.
5. Use zh-CN as the primary language for text fields.
6. difficulty must be one of: easy, medium, hard.
7. field must be one of: science, art, sports, politics, business, entertainment, literature, military, fictional, other.
8. If the target is a fictional character, field MUST be "fictional" and era should describe the work's era or setting (e.g. "虚构", "明代小说", "1990年代游戏").

The returned JSON must match this schema:
{
  "name": "string (canonical name, e.g. 爱因斯坦 or 孙悟空)",
  "zhName": "string (canonical Chinese name)",
  "enName": "string (canonical English name)",
  "era": "string (e.g. 1879-1955, 20世纪, 当代, 虚构)",
  "field": "string (one of the allowed values)",
  "difficulty": "string (easy|medium|hard)",
  "brief": "string (one-sentence summary, do NOT include the name or the work's title)",
  "aliases": ["string list of common Chinese/English aliases, nicknames or alternative translations"]
}`;

function buildUserPrompt(difficulty, field, excludeList = []) {
  const lines = [];
  lines.push(`Difficulty: ${difficulty}`);
  if (field === 'fictional') {
    lines.push('Field: fictional. You MUST pick a famous fictional character. Vary the source: novels, movies, TV series, video games, animation, anime/manga, fairy tales, myths, legends, folk tales — do NOT always default to 西游记 or Disney. Match the requested difficulty to how widely known the character is.');
  } else if (field && field !== 'any') {
    lines.push(`Field: ${field}. Pick a real, famous person from this field (not a fictional character).`);
  } else {
    lines.push('Field: any. You may freely choose either a real famous person or a famous fictional character — pick one at random and keep it different from the already-used names.');
  }
  if (excludeList.length > 0) {
    lines.push(`Please avoid these already-used names: ${excludeList.join(', ')}`);
  }
  lines.push('Now generate a JSON object for the chosen figure. Remember: no markdown, only JSON.');
  return lines.join('\n');
}

function validateCelebrity(obj) {
  if (!obj || typeof obj !== 'object') {
    throw new Error('Invalid celebrity object');
  }

  const required = ['name', 'zhName', 'enName', 'era', 'field', 'difficulty', 'brief', 'aliases'];
  for (const key of required) {
    if (!(key in obj)) {
      throw new Error(`Missing field in celebrity JSON: ${key}`);
    }
  }

  if (!VALID_FIELDS.includes(obj.field)) {
    throw new Error(`Invalid field: ${obj.field}`);
  }
  if (!VALID_DIFFICULTIES.includes(obj.difficulty)) {
    throw new Error(`Invalid difficulty: ${obj.difficulty}`);
  }
  if (!Array.isArray(obj.aliases)) {
    throw new Error('aliases must be an array');
  }

  return obj;
}

export async function generateCelebrity(client, { difficulty, field, excludeList = [] }) {
  const messages = [
    { role: 'system', content: GENERATION_SYSTEM_PROMPT },
    { role: 'user', content: buildUserPrompt(difficulty, field, excludeList) },
  ];

  const response = await client.send(messages, {
    responseFormat: { type: 'json_object' },
  });

  const parsed = extractJson(response);
  return validateCelebrity(parsed);
}
